import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { InputNumberWithSuffix } from '@/components/ui/InputNumberWithSuffix';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  fetchParticipationStatuses,
  replaceParticipationStatuses,
  updateWebinar,
} from '@/features/webinar/webinarThunks';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';
import { Switch } from '@/components/ui/switch';
import ColorSelector from '@/components/ui/ColorSelector';
import { useTranslation } from 'react-i18next';
import { LucideIconPopoverPicker, LucideLazyIcon } from './LucideIconPopoverPicker';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import Title from '@/components/ui/Title';
import { cn } from '@/lib/utils';
import type { ParticipationStatusBulkInput } from '@/features/webinar/types';

type StatusFormValues = {
  statuses: Array<{
    criteria:
      | 'PRESENTER'
      | 'MODERATOR'
      | 'FIRST_SCORE'
      | 'SECOND_SCORE'
      | 'THIRD_SCORE'
      | 'TOP_25_PERCENT'
      | 'TOP_50_PERCENT'
      | 'TOP_75_PERCENT'
      | 'TOP_100_PERCENT'
      | 'CONVERSION';
    label: string;
    color: string;
    customColor?: string;
    icon?: string;
  }>;
};

const getStatusSchema = (t: (key: string) => string) =>
  z.object({
    statuses: z.array(
      z.object({
        criteria: z.enum(
          [
            'PRESENTER',
            'MODERATOR',
            'FIRST_SCORE',
            'SECOND_SCORE',
            'THIRD_SCORE',
            'TOP_25_PERCENT',
            'TOP_50_PERCENT',
            'TOP_75_PERCENT',
            'TOP_100_PERCENT',
            'CONVERSION',
          ],
          {
            message: t('viewerStatus.validation.criteriaRequired'),
          },
        ),
        label: z
          .string()
          .min(1, t('viewerStatus.validation.labelRequired'))
          .max(50, t('viewerStatus.validation.labelMaxLength'))
          .refine((val) => val.trim().length > 0, {
            message: t('viewerStatus.validation.labelRequired'),
          }),
        color: z
          .string()
          .min(1, t('viewerStatus.validation.colorRequired'))
          .refine(
            (val) => {
              if (val === 'custom') return true;
              if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(val)) return true;
              const validIds = [
                'accent',
                'background',
                'contrast',
                'secondary-background',
                'black',
                'white',
              ];
              return validIds.includes(val);
            },
            {
              message: t('viewerStatus.validation.colorInvalid'),
            },
          ),
        customColor: z
          .string()
          .regex(
            /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/,
            t('viewerStatus.validation.customColorInvalid'),
          )
          .optional(),
        icon: z.string().optional(),
      }),
    ),
  });

export default function ViewerStatus({
  webinarId,
  workspaceId,
  isEdit,
  isEvergreen,
  openTrigger,
  onThresholdSaved,
  readOnly,
}: {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  isEvergreen?: boolean;
  openTrigger?: number;
  onThresholdSaved?: () => void;
  readOnly?: boolean;
}) {
  const locked = readOnly === true;
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();

  const criteriaOptions = useMemo(
    () => [
      { value: 'PRESENTER', label: t('viewerStatus.criteria.presenter') },
      { value: 'MODERATOR', label: t('viewerStatus.criteria.moderator') },
      { value: 'FIRST_SCORE', label: t('viewerStatus.criteria.firstScore') },
      { value: 'SECOND_SCORE', label: t('viewerStatus.criteria.secondScore') },
      { value: 'THIRD_SCORE', label: t('viewerStatus.criteria.thirdScore') },
      { value: 'TOP_25_PERCENT', label: t('viewerStatus.criteria.top25Percent') },
      { value: 'TOP_50_PERCENT', label: t('viewerStatus.criteria.top50Percent') },
      { value: 'TOP_75_PERCENT', label: t('viewerStatus.criteria.top75Percent') },
      { value: 'TOP_100_PERCENT', label: t('viewerStatus.criteria.top100Percent') },
      { value: 'CONVERSION', label: t('viewerStatus.criteria.conversion') },
    ],
    [t],
  );
  const visualIdentity = useAppSelector(
    (s: RootState) => s.workspace.currentWorkspace?.visualIdentity,
  );
  const participationStatuses = useAppSelector((s: RootState) => s.webinar.participationStatuses);
  const showParticipationStatus = useAppSelector(
    (s: RootState) => s.webinar.showParticipationStatus,
  );
  const participationStatusesLoading = useAppSelector(
    (s: RootState) => s.webinar.participationStatusesLoading,
  );
  const currentWebinar = useAppSelector((s: RootState) => s.webinar.currentWebinar);

  console.log('participationStatuses', participationStatuses);
  const hasInitialized = useRef(false);

  const defaultColors = useMemo(
    () => [
      {
        id: 'accent',
        name: t('participationStatus.colors.accent'),
        value: visualIdentity?.accentColor,
        color: visualIdentity?.accentColor,
      },
      {
        id: 'background',
        name: t('participationStatus.colors.background'),
        value: visualIdentity?.backgroundColor,
        color: visualIdentity?.backgroundColor,
      },
      {
        id: 'contrast',
        name: t('participationStatus.colors.contrast'),
        value: visualIdentity?.contrastColor,
        color: visualIdentity?.contrastColor,
      },
      {
        id: 'secondary-background',
        name: t('participationStatus.colors.secondaryBackground'),
        value: visualIdentity?.secondaryBackgroundColor,
        color: visualIdentity?.secondaryBackgroundColor,
      },
      {
        id: 'black',
        name: t('participationStatus.colors.black'),
        value: '#000000',
        color: '#000000',
      },
      {
        id: 'white',
        name: t('participationStatus.colors.white'),
        value: '#FFFFFF',
        color: '#FFFFFF',
      },
    ],
    [visualIdentity, t],
  );

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'custom') {
      return customColor || '#2C6CFF';
    }
    const color = defaultColors.find((c) => c.id === colorId);
    return color?.value || color?.color || '#2C6CFF';
  };

  const getColorIdByValue = (hexValue: string): string => {
    if (!hexValue || !hexValue.startsWith('#')) {
      return 'accent';
    }
    const normalizedHex = hexValue.trim().toUpperCase();

    const matchingColor = defaultColors.find(
      (c) => c.value?.toUpperCase() === normalizedHex || c.color?.toUpperCase() === normalizedHex,
    );

    if (matchingColor) {
      return matchingColor.id;
    }

    return 'custom';
  };

  const getFinalColor = (colorFieldName: string) => {
    const colorId = form.watch(colorFieldName as any);
    const customColorFieldName = colorFieldName.replace('.color', '.customColor');
    const customColor = form.watch(customColorFieldName as any);
    return getColorValueById(colorId || '', customColor);
  };

  const getStyle = (colorId: string, customColor?: string) => {
    return {
      backgroundColor: getColorValueById(colorId, customColor),
    };
  };

  const statusSchema = useMemo(() => getStatusSchema(t), [t]);

  const form = useForm<StatusFormValues>({
    resolver: zodResolver(statusSchema),
    defaultValues: {
      statuses: [
        {
          criteria: 'FIRST_SCORE',
          label: '',
          color: 'accent',
          icon: undefined as any,
        },
      ],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'statuses',
  });

  useEffect(() => {
    dispatch(fetchParticipationStatuses({ workspaceId, webinarId }));
  }, [dispatch, workspaceId, webinarId]);

  useEffect(() => {
    if (
      participationStatuses &&
      participationStatuses.length > 0 &&
      !participationStatusesLoading &&
      !hasInitialized.current
    ) {
      form.reset({
        statuses: participationStatuses.map((status) => {
          const colorId = getColorIdByValue(status.color);
          const isCustom = colorId === 'custom';

          return {
            criteria: status.criteria,
            label: status.label,
            color: colorId,
            customColor: isCustom ? status.color : undefined,
            icon: status.icon,
          };
        }),
      });
      hasInitialized.current = true;
    }
  }, [participationStatuses, participationStatusesLoading, form, defaultColors]);

  useEffect(() => {
    if (!participationStatusesLoading) {
      setIsSwitchEnabled(showParticipationStatus);
    }
  }, [showParticipationStatus, participationStatusesLoading]);

  useEffect(() => {
    if (currentWebinar?.displayParticipantsMinThreshold !== undefined) {
      const thresholdValue = currentWebinar.displayParticipantsMinThreshold || 0;
      setInputValue(thresholdValue.toString());
      setAttendeeCount(thresholdValue);
    }
  }, [currentWebinar?.displayParticipantsMinThreshold]);

  const addStatus = () => {
    if (locked) return;
    append({
      criteria: undefined as any,
      label: '',
      color: 'accent',
      icon: undefined as any,
    });
  };

  const buildStatusesInput = (): ParticipationStatusBulkInput => {
    if (!isSwitchEnabled) {
      return {
        showStatus: false,
        statuses: participationStatuses.map(({ criteria, label, color, icon }) => ({
          criteria,
          label,
          color,
          icon: icon || '',
        })),
      };
    }
    return {
      showStatus: true,
      statuses: form.getValues('statuses').map((status) => ({
        criteria: status.criteria,
        label: status.label,
        color: getColorValueById(
          status.color === 'custom' ? 'custom' : status.color,
          status.customColor,
        ),
        icon: status.icon || '',
      })),
    };
  };

  const saveStatuses = async (): Promise<boolean> => {
    try {
      await dispatch(
        replaceParticipationStatuses({
          workspaceId,
          webinarId,
          input: buildStatusesInput(),
        }),
      ).unwrap();
      return true;
    } catch (error: any) {
      const validationErrors = error?.payload?.details;
      if (Array.isArray(validationErrors)) {
        validationErrors.forEach((err: { fieldName?: string; message?: string }) => {
          const fieldName = err.fieldName;
          const message = err.message || t('viewerStatus.messages.validationError');
          if (fieldName) {
            const match = fieldName.match(/statuses?\.(\d+)\.(\w+)/);
            if (match) {
              const [, index, field] = match;
              form.setError(`statuses.${index}.${field}` as any, {
                type: 'server',
                message: message,
              });
            } else {
              toast.error(`${fieldName}: ${message}`);
            }
          } else {
            toast.error(message);
          }
        });
      } else {
        const errorMessage =
          error?.payload?.message || error?.message || t('viewerStatus.messages.saveError');
        toast.error(errorMessage);
      }
      return false;
    }
  };

  const formData = form.watch('statuses');
  const { errors } = form.formState;
  const [isSwitchEnabled, setIsSwitchEnabled] = useState(showParticipationStatus);
  const [inputValue, setInputValue] = useState(
    currentWebinar?.displayParticipantsMinThreshold?.toString() || '0',
  );
  const [attendeeCount, setAttendeeCount] = useState(
    currentWebinar?.displayParticipantsMinThreshold || 0,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string>(isEvergreen ? '' : 'accordion-1');

  useEffect(() => {
    if (openTrigger && openTrigger > 0) {
      setAccordionValue('accordion-1');
    }
  }, [openTrigger]);

  const getAvailableCriteria = (currentIndex: number) => {
    const selectedCriteria: string[] = [];
    formData.forEach((status, idx) => {
      if (idx !== currentIndex && status.criteria) {
        selectedCriteria.push(status.criteria);
      }
    });
    return criteriaOptions.filter((option) => !selectedCriteria.includes(option.value));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      setInputValue('0');
      setAttendeeCount(0);
      return;
    }

    const cleanValue = value.replace(/^0+/, '') || '0';
    const numericValue = parseInt(cleanValue, 10);

    if (isNaN(numericValue) || numericValue < 0) {
      return;
    }

    setInputValue(cleanValue);
    setAttendeeCount(numericValue);
  };

  const handleSave = async () => {
    if (locked) return;
    if (!currentWebinar) {
      toast.error(t('viewerStatus.messages.webinarNotFound'));
      return;
    }
    if (isSwitchEnabled && !(await form.trigger('statuses'))) return;

    setIsSaving(true);
    try {
      if (!(await saveStatuses())) return;
      await dispatch(
        updateWebinar({
          workspaceId,
          id: webinarId,
          input: {
            title: currentWebinar?.title,
            shortDescription: currentWebinar?.shortDescription,
            description: currentWebinar?.description,
            displayParticipantsMinThreshold: attendeeCount,
          },
        }),
      ).unwrap();
      toast.success(t('viewerStatus.messages.saved'));
      setAccordionValue('');
      onThresholdSaved?.();
    } catch (error: any) {
      const errorMessage =
        error?.message || error?.payload?.message || t('viewerStatus.messages.saveError');
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const hasStatuses = participationStatuses && participationStatuses.length > 0;

  return (
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
      <AccordionItem
        value="accordion-1"
        className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
      >
        <AccordionTrigger className=" rounded-[20px]" fillForm={hasStatuses}>
          <div className="flex flex-col gap-2">
            <Title level={2}>{t('viewerStatus.title')}</Title>
            <p className="text-sm font-normal text-tertiary">{t('viewerStatus.description')}</p>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-6">
            <div
              className={cn(
                'flex flex-col gap-6',
                locked && 'pointer-events-none select-none opacity-90',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <Label className="text-secondary">
                  {t('viewerStatus.displayParticipantsThreshold')}
                </Label>
                <InputNumberWithSuffix
                  label={t('viewerStatus.numberLabel')}
                  suffix={t('viewerStatus.people')}
                  value={inputValue}
                  onChange={handleInputChange}
                  disabled={locked}
                  min={0}
                  maxLength={6}
                />
              </div>
              <div className="pb-4 flex items-center justify-between w-full">
                <Label>{t('viewerStatus.assignStatuses')}</Label>
                <Switch
                  checked={isSwitchEnabled}
                  onCheckedChange={setIsSwitchEnabled}
                  disabled={locked}
                />
              </div>
              {isSwitchEnabled && (
                <div className="p-6 rounded-[20px] bg-medium">
                  <div>
                    <div className="flex flex-col gap-4">
                      {fields.map((field, index) => {
                        const availableCriteria = getAvailableCriteria(index);
                        const currentCriteria = formData[index]?.criteria || '';

                        return (
                          <div key={field.id} className="flex items-start gap-4">
                            <div className="flex-1">
                              <Controller
                                name={`statuses.${index}.criteria`}
                                control={form.control}
                                render={({ field: criteriaField }) => (
                                  <div>
                                    <Select
                                      value={criteriaField.value}
                                      onValueChange={criteriaField.onChange}
                                    >
                                      <SelectTrigger
                                        label={t('viewerStatus.statusLabel')}
                                        className="h-14"
                                      >
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {availableCriteria.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                        {currentCriteria &&
                                          !availableCriteria.some(
                                            (opt) => opt.value === currentCriteria,
                                          ) && (
                                            <SelectItem value={currentCriteria}>
                                              {criteriaOptions.find(
                                                (opt) => opt.value === currentCriteria,
                                              )?.label || currentCriteria}
                                            </SelectItem>
                                          )}
                                      </SelectContent>
                                    </Select>
                                    {errors.statuses?.[index]?.criteria && (
                                      <p className="text-sm text-red-500 mt-1">
                                        {errors.statuses[index]?.criteria?.message}
                                      </p>
                                    )}
                                  </div>
                                )}
                              />
                            </div>

                            <div className="flex-1">
                              <Controller
                                name={`statuses.${index}.label`}
                                control={form.control}
                                render={({ field: labelField }) => (
                                  <TextFieldWithLimit
                                    label={t('viewerStatus.labelLabel')}
                                    value={labelField.value ?? ''}
                                    onChange={(v) => labelField.onChange(v)}
                                    error={errors.statuses?.[index]?.label?.message}
                                    maxLength={50}
                                  />
                                )}
                              />
                            </div>

                            <div className="flex-1">
                              <ColorSelector
                                form={form}
                                offerType="A"
                                colorsArray={defaultColors}
                                colorFieldName={`statuses.${index}.color`}
                                customColorFieldName={`statuses.${index}.customColor`}
                                label={t('viewerStatus.colorLabel')}
                                customColorLabel={t('viewerStatus.customColorLabel')}
                                showCustomColor={true}
                                defaultValue="#2C6CFF"
                                getFinalColor={() => getFinalColor(`statuses.${index}.color`)}
                                getStyle={(colorId, customColor) => getStyle(colorId, customColor)}
                              />
                              {errors.statuses?.[index]?.color && (
                                <p className="text-sm text-red-500 mt-1">
                                  {errors.statuses[index]?.color?.message}
                                </p>
                              )}
                            </div>

                            <div className="w-1/10">
                              <Controller
                                name={`statuses.${index}.icon`}
                                control={form.control}
                                render={({ field: iconField }) => (
                                  <div>
                                    <LucideIconPopoverPicker
                                      value={iconField.value as any}
                                      onChange={(icon) => {
                                        iconField.onChange(icon === undefined ? '' : icon);
                                      }}
                                    />

                                    {errors.statuses?.[index]?.icon && (
                                      <p className="text-sm text-red-500 mt-1">
                                        {errors.statuses[index]?.icon?.message}
                                      </p>
                                    )}
                                  </div>
                                )}
                              />
                            </div>

                            {fields.length > 1 && (
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="bg-white rounded-lg py-3 px-2 border border-input cursor-pointer shrink-0"
                              >
                                <Trash2 className="size-6" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {fields.length < 10 && (
                      <button
                        type="button"
                        onClick={addStatus}
                        className="flex items-center gap-2 pt-4 cursor-pointer"
                      >
                        <Plus className="size-6" />
                        <Label className="cursor-pointer text-sm">
                          {t('viewerStatus.addStatus')}
                        </Label>
                      </button>
                    )}
                  </div>

                  <Accordion
                    type="single"
                    collapsible
                    defaultValue={isEvergreen ? undefined : 'item-1'}
                  >
                    <AccordionItem value="item-1">
                      <AccordionTrigger>{t('viewerStatus.preview')}</AccordionTrigger>
                      <AccordionContent className="rounded-[20px]">
                        <div
                          className="flex flex-col gap-4 max-w-sm mx-auto rounded-[20px]"
                          style={{
                            backgroundColor: visualIdentity?.backgroundColor || '#FFFFFF',
                          }}
                        >
                          <div
                            style={{
                              backgroundColor:
                                visualIdentity?.theme === 'LIGHT'
                                  ? 'rgba(31, 31, 31, 0.08)'
                                  : 'rgba(255, 255, 255, 0.2)',
                            }}
                            className="p-4 flex flex-col gap-4 rounded-[20px]"
                          >
                            {formData.map((status, index) => {
                              const isLucideIcon =
                                status.icon &&
                                typeof status.icon === 'string' &&
                                status.icon in dynamicIconImports;
                              const displayColor = getColorValueById(
                                status.color as any,
                                status.customColor,
                              );
                              const isRoleStatus =
                                status.criteria === 'PRESENTER' || status.criteria === 'MODERATOR';

                              return (
                                <div
                                  key={fields[index]?.id || index}
                                  className={`flex flex-col p-2 mx-2 rounded-lg rounded-bl-none  bg-white ${
                                    isRoleStatus ? 'border' : ''
                                  }`}
                                  style={{
                                    backgroundColor: visualIdentity?.backgroundColor || '#FFFFFF',
                                    ...(isRoleStatus && { borderColor: displayColor }),
                                  }}
                                >
                                  <div className="flex items-center gap-1">
                                    {isLucideIcon ? (
                                      <div className="shrink-0">
                                        <LucideLazyIcon
                                          name={status.icon as any}
                                          width={14}
                                          height={14}
                                          color={displayColor}
                                        />
                                      </div>
                                    ) : null}
                                    <p
                                      className={`text-xs ${
                                        isRoleStatus ? 'font-bold' : 'font-normal'
                                      } ${
                                        visualIdentity?.theme === 'LIGHT'
                                          ? 'text-secondary'
                                          : 'text-white'
                                      }`}
                                      style={{
                                        color: displayColor,
                                      }}
                                    >
                                      {t('viewerStatus.previewFirstName')}{' '}
                                      {status.label && (
                                        <span
                                          className={isRoleStatus ? 'font-bold' : 'text-tertiary'}
                                        >
                                          - {status.label}
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <p
                                    className={`text-base ${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
                                  >
                                    Lorem, ipsum dolor sit amet consectetur.
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </div>

            <Button
              className="w-full"
              disabled={locked || isSaving}
              type="button"
              onClick={handleSave}
            >
              {isSaving && <Loader />}
              {t('viewerStatus.save')}
            </Button>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

{
  /* <button
            type="button"
            // onClick={onRemove}
            className="bg-white rounded-lg mr-4 py-3 px-2 border border-input cursor-pointer"
          >
            <Trash2 className="size-6" />
          </button> */
}

{
  /* <div className="flex items-center justify-between w-full">
<Label>
  Assigner des statut de participation{' '}
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="text-tertiary">
        <CircleAlert size={24} className="text-tertiary" />
      </span>
    </TooltipTrigger>
    <TooltipContent side="right">
      <p>
        Les statuts de participation seront assignés en fonctions des actions des
        participants.
      </p>
    </TooltipContent>
  </Tooltip>
</Label>
<Switch />
</div> */
}
